import { ActionIcon, Alert, Button, Tabs, Tooltip } from '@mantine/core';
import {
  IconAlertTriangle,
  IconChecklist,
  IconFileImport,
  IconPencil,
  IconPlus,
  IconRosetteDiscountCheck,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import classes from './Compliance.module.css';
import { TableSkeleton, ViewError, ViewHead } from './SurveySchedule.js';
import type {
  CeAuthorization,
  ClassDueRow,
  ClassDueState,
  ClassFinding,
  ClassItemList,
} from './types.js';

export const REGULATORY_TABS = ['findings', 'items', 'due', 'authorizations'] as const;
export type RegulatoryTab = (typeof REGULATORY_TABS)[number];

const DUE_TONE: Record<ClassDueState, StatusTone> = {
  never_surveyed: 'neutral',
  not_credited: 'danger',
  awaiting_confirmation: 'warning',
  confirmed: 'success',
  overdue: 'danger',
};

/** Yüklenen tablo: `null` yükleniyor, `error` okunamadı. */
export interface Loaded<T> {
  data: T | null;
  error: string | null;
}

export interface RegulatoryRequirementsProps {
  tab: RegulatoryTab;
  onTab: (tab: RegulatoryTab) => void;
  findings: Loaded<ClassFinding[]>;
  items: Loaded<ClassItemList>;
  due: Loaded<ClassDueRow[]>;
  authorizations: Loaded<CeAuthorization[]>;
  onRetry: () => void;
  onBack: () => void;
  /** Ofis formları (K25, K31); gemide yok. */
  office?:
    | {
        addFinding: () => void;
        editFinding: (finding: ClassFinding) => void;
        addAuthorization: () => void;
        editAuthorization: (authorization: CeAuthorization) => void;
        importItems: () => void;
      }
    | undefined;
  /** Gemide sörveyör teyidi (K31). */
  onConfirm?: (() => void) | undefined;
}

function Table({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section
      className={classes.tableBox}
      data-cards
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={classes.table} data-cards>
        {children}
      </table>
    </section>
  );
}

/**
 * Regulatory Requirements: klas bulguları (CoC; açık ve gecikmiş CoC ekipmanı PMS dışı yapar, K25),
 * BV kalem listesi (NR467 App 1 2.4.1), Class Due List (başmühendis sörveyi ve sörveyör teyidi;
 * K31) ve başmühendis yetki beyanları. Müşteri ekranında yok; aynı tablo dili (ADR 0019).
 */
export function RegulatoryRequirements({
  tab,
  onTab,
  findings,
  items,
  due,
  authorizations,
  onRetry,
  onBack,
  office,
  onConfirm,
}: RegulatoryRequirementsProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.regulatory.${key}`, values);

  const state = <T,>(loaded: Loaded<T>, render: (data: T) => ReactNode) =>
    loaded.error ? (
      <ViewError message={loaded.error} onRetry={onRetry} />
    ) : loaded.data === null ? (
      <TableSkeleton />
    ) : (
      render(loaded.data)
    );

  const toolbar = (children: ReactNode) =>
    children ? (
      <div className={`${classes.viewActions} ${classes.tabToolbar}`}>{children}</div>
    ) : null;

  const findingsTab = state(findings, (rows) => (
    <>
      {toolbar(
        office ? (
          <Button
            size="xs"
            leftSection={<IconPlus size={14} aria-hidden />}
            onClick={office.addFinding}
          >
            {k('findings.add')}
          </Button>
        ) : null,
      )}
      {rows.length === 0 ? (
        <EmptyState title={k('findings.empty')} />
      ) : (
        <Table label={k('findings.table')}>
          <thead>
            <tr>
              <th scope="col">{k('findings.columns.number')}</th>
              <th scope="col">{k('findings.columns.kind')}</th>
              <th scope="col">{k('findings.columns.text')}</th>
              <th scope="col">{k('findings.columns.equipment')}</th>
              <th scope="col">{k('findings.columns.imposed')}</th>
              <th scope="col">{k('findings.columns.due')}</th>
              <th scope="col">{k('findings.columns.status')}</th>
              {office ? (
                <th scope="col" data-center>
                  {k('findings.columns.actions')}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => {
              const status = f.closedOn ? 'closed' : f.overdue ? 'overdue' : 'open';
              return (
                <tr key={f.id}>
                  <td data-label={k('findings.columns.number')} className={classes.nowrap}>
                    {f.number}
                  </td>
                  <td data-label={k('findings.columns.kind')}>
                    {k(`findings.kinds.${f.kind}`, { defaultValue: f.kind })}
                  </td>
                  <td data-label={k('findings.columns.text')}>{f.text}</td>
                  <td data-label={k('findings.columns.equipment')}>{f.equipmentCode ?? '—'}</td>
                  <td data-label={k('findings.columns.imposed')} className={classes.nowrap}>
                    {formatDay(f.imposedOn)}
                  </td>
                  <td data-label={k('findings.columns.due')} className={classes.nowrap}>
                    {formatDay(f.dueOn)}
                  </td>
                  <td data-label={k('findings.columns.status')}>
                    <StatusBadge
                      tone={
                        status === 'closed'
                          ? 'success'
                          : status === 'overdue'
                            ? 'danger'
                            : 'warning'
                      }
                    >
                      {k(`findings.states.${status}`)}
                      {status === 'closed' ? ` ${formatDay(f.closedOn)}` : ''}
                    </StatusBadge>
                  </td>
                  {office ? (
                    <td data-label={k('findings.columns.actions')} data-center>
                      <Tooltip label={k('findings.edit', { number: f.number })} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={k('findings.edit', { number: f.number })}
                          onClick={() => office.editFinding(f)}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  ));

  const itemsTab = state(items, (list) => (
    <>
      {toolbar(
        office ? (
          <Button
            size="xs"
            variant="default"
            leftSection={<IconFileImport size={14} aria-hidden />}
            onClick={office.importItems}
          >
            {k('items.import')}
          </Button>
        ) : null,
      )}
      {list.warnings.length > 0 ? (
        <Alert
          color="yellow"
          icon={<IconAlertTriangle size={16} />}
          className={classes.warningList}
          role="status"
        >
          {k('items.warnings', {
            codes: list.warnings
              .map((w) => `${w.equipmentCode ?? '—'} (${w.bvItemCode})`)
              .join(', '),
          })}
        </Alert>
      ) : null}
      {list.items.length === 0 ? (
        <EmptyState
          title={k('items.empty')}
          description={office ? k('items.emptyHint') : undefined}
        />
      ) : (
        <Table label={k('items.table')}>
          <thead>
            <tr>
              <th scope="col">{k('items.columns.code')}</th>
              <th scope="col">{k('items.columns.description')}</th>
              <th scope="col">{k('items.columns.category')}</th>
              <th scope="col">{k('items.columns.principle')}</th>
              <th scope="col" data-numeric>
                {k('items.columns.interval')}
              </th>
              <th scope="col">{k('items.columns.ce')}</th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((item) => (
              <tr key={item.id}>
                <td data-label={k('items.columns.code')} className={classes.nowrap}>
                  {item.itemCode}
                </td>
                <td data-label={k('items.columns.description')}>{item.description}</td>
                <td data-label={k('items.columns.category')}>
                  {k(`items.categories.${item.category}`, { defaultValue: item.category })}
                </td>
                <td data-label={k('items.columns.principle')}>
                  {k(`items.principles.${item.surveyPrinciple}`, {
                    defaultValue: item.surveyPrinciple,
                  })}
                </td>
                <td data-label={k('items.columns.interval')} data-numeric>
                  {item.intervalMonths ?? '—'}
                </td>
                <td data-label={k('items.columns.ce')}>
                  <StatusBadge tone={item.ceAllowed ? 'success' : 'neutral'}>
                    {item.ceAllowed ? k('items.ceAllowed') : k('items.ceNotAllowed')}
                  </StatusBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  ));

  const dueTab = state(due, (rows) => (
    <>
      {toolbar(
        onConfirm ? (
          <Button
            size="xs"
            leftSection={<IconRosetteDiscountCheck size={14} aria-hidden />}
            onClick={onConfirm}
          >
            {k('due.confirm')}
          </Button>
        ) : null,
      )}
      {rows.length === 0 ? (
        <EmptyState title={k('due.empty')} />
      ) : (
        <Table label={k('due.table')}>
          <thead>
            <tr>
              <th scope="col">{k('due.columns.item')}</th>
              <th scope="col">{k('due.columns.equipment')}</th>
              <th scope="col">{k('due.columns.lastSurvey')}</th>
              <th scope="col">{k('due.columns.confirmation')}</th>
              <th scope="col">{k('due.columns.due')}</th>
              <th scope="col">{k('due.columns.state')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.itemId}>
                <td data-label={k('due.columns.item')}>
                  <strong>{row.itemCode}</strong>
                  <span className={classes.nameNote}>{row.description}</span>
                </td>
                <td data-label={k('due.columns.equipment')}>
                  {row.equipment.length > 0 ? row.equipment.map((e) => e.code).join(', ') : '—'}
                </td>
                <td data-label={k('due.columns.lastSurvey')}>
                  {row.lastCeSurvey ? (
                    <>
                      <span className={classes.nowrap}>{formatDay(row.lastCeSurvey.doneOn)}</span>
                      {!row.lastCeSurvey.ceCredit ? (
                        <span className={classes.nameNote}>
                          {k('due.notCredited', {
                            reason: t(
                              `vesselCertificates.errors.${row.lastCeSurvey.ceRejectionCode ?? 'CE_NOT_CREDITED'}`,
                              { defaultValue: row.lastCeSurvey.ceRejectionCode ?? '' },
                            ),
                          })}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td data-label={k('due.columns.confirmation')}>
                  {row.confirmation
                    ? k('due.confirmedBy', {
                        date: formatDay(row.confirmation.confirmedOn),
                        surveyor: row.confirmation.surveyor,
                      })
                    : '—'}
                </td>
                <td data-label={k('due.columns.due')} className={classes.nowrap}>
                  {formatDay(row.dueOn)}
                </td>
                <td data-label={k('due.columns.state')}>
                  <StatusBadge tone={DUE_TONE[row.state]}>
                    {k(`due.states.${row.state}`)}
                  </StatusBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  ));

  const authorizationsTab = state(authorizations, (rows) => (
    <>
      {toolbar(
        office ? (
          <Button
            size="xs"
            leftSection={<IconPlus size={14} aria-hidden />}
            onClick={office.addAuthorization}
          >
            {k('authorizations.add')}
          </Button>
        ) : null,
      )}
      {rows.length === 0 ? (
        <EmptyState title={k('authorizations.empty')} />
      ) : (
        <Table label={k('authorizations.table')}>
          <thead>
            <tr>
              <th scope="col">{k('authorizations.columns.person')}</th>
              <th scope="col">{k('authorizations.columns.from')}</th>
              <th scope="col">{k('authorizations.columns.to')}</th>
              <th scope="col">{k('authorizations.columns.ref')}</th>
              <th scope="col">{k('authorizations.columns.state')}</th>
              {office ? (
                <th scope="col" data-center>
                  {k('authorizations.columns.actions')}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id}>
                <td data-label={k('authorizations.columns.person')}>
                  {a.userName ?? k('authorizations.unknownPerson')}
                </td>
                <td data-label={k('authorizations.columns.from')} className={classes.nowrap}>
                  {formatDay(a.validFrom)}
                </td>
                <td data-label={k('authorizations.columns.to')} className={classes.nowrap}>
                  {a.validTo ? formatDay(a.validTo) : k('authorizations.openEnded')}
                </td>
                <td data-label={k('authorizations.columns.ref')}>{a.declarationRef}</td>
                <td data-label={k('authorizations.columns.state')}>
                  <StatusBadge tone={a.validToday ? 'success' : 'neutral'}>
                    {a.validToday ? k('authorizations.valid') : k('authorizations.notValid')}
                  </StatusBadge>
                </td>
                {office ? (
                  <td data-label={k('authorizations.columns.actions')} data-center>
                    <Tooltip
                      label={k('authorizations.edit', { ref: a.declarationRef })}
                      withinPortal
                    >
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        aria-label={k('authorizations.edit', { ref: a.declarationRef })}
                        onClick={() => office.editAuthorization(a)}
                      >
                        <IconPencil size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  ));

  return (
    <Panel>
      <ViewHead title={k('title')} icon={<IconChecklist size={18} />} onBack={onBack} />
      <Tabs
        value={tab}
        onChange={(value) => {
          if (value) onTab(value as RegulatoryTab);
        }}
        className={classes.tabs}
        keepMounted={false}
      >
        <Tabs.List aria-label={k('tabs.label')}>
          {REGULATORY_TABS.map((key) => (
            <Tabs.Tab key={key} value={key}>
              {k(`tabs.${key}`)}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <Tabs.Panel value="findings" pt={12}>
          {findingsTab}
        </Tabs.Panel>
        <Tabs.Panel value="items" pt={12}>
          {itemsTab}
        </Tabs.Panel>
        <Tabs.Panel value="due" pt={12}>
          {dueTab}
        </Tabs.Panel>
        <Tabs.Panel value="authorizations" pt={12}>
          {authorizationsTab}
        </Tabs.Panel>
      </Tabs>
    </Panel>
  );
}
